/**
 * Contrast-corrected ring colors for user-chosen swatch colors.
 *
 * Color swatches (tag dots, palette squares) carry a color the *user* picked, so
 * the fill has to render exactly as chosen — it is the item's identity, and two
 * different palette entries must never resolve to the same rendered color.
 *
 * That leaves the fill free to sit at any lightness, including one that matches
 * the surface behind it: the palette's darkest tones land within ~1.1:1 of the
 * dark-mode surface and its palest tones within ~1.1:1 of the light-mode one, so
 * the swatch visually disappears.
 *
 * So the visibility rides on an outline instead of the fill. This module returns,
 * for a given fill, the same hue and saturation moved in lightness until it
 * clears WCAG 1.4.11's 3:1 non-text minimum against the swatch surface — away
 * from the surface in each theme (lighter on dark, darker on light). A fill that
 * already clears 3:1 comes back unchanged, so its ring is indistinguishable from
 * the fill and only the swatches that need an edge show one.
 */

import { primitives } from "./primitives"

/** WCAG 1.4.11 non-text contrast minimum — swatch rings, borders, icons. */
const NON_TEXT_CONTRAST = 3

/** WCAG 1.4.3 body-text contrast minimum. */
const TEXT_CONTRAST = 4.5

/**
 * Swatches render on `surface-1` (Badge default fill, Tooltip, the tag overflow
 * pill). `--surface-1` maps to primaryWarm 100 in light and 700 in dark.
 */
const SWATCH_SURFACE = {
  light: primitives.primaryWarm[100],
  dark: primitives.primaryWarm[700],
} as const

export interface SwatchRingColors {
  light: string
  dark: string
}

const HEX_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

type Rgb = [number, number, number]
type Hsl = [number, number, number]

function parseHex(hex: string): Rgb | null {
  const value = hex.trim()
  if (!HEX_PATTERN.test(value)) {
    return null
  }
  const body = value.slice(1)
  const full =
    body.length === 3
      ? body
          .split("")
          .map((char) => char + char)
          .join("")
      : body
  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ]
}

/** Snaps to the 8-bit channels the color will actually render with. */
function quantize([r, g, b]: Rgb): Rgb {
  const channel = (value: number) =>
    Math.round(Math.min(255, Math.max(0, value)))
  return [channel(r), channel(g), channel(b)]
}

function toHex(rgb: Rgb): string {
  return `#${quantize(rgb)
    .map((channel) => channel.toString(16).padStart(2, "0"))
    .join("")}`
}

function linearize(channel: number): number {
  const ratio = channel / 255
  return ratio <= 0.03928 ? ratio / 12.92 : ((ratio + 0.055) / 1.055) ** 2.4
}

function relativeLuminance([r, g, b]: Rgb): number {
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b)
}

function contrastRatio(a: Rgb, b: Rgb): number {
  const [lighter, darker] = [relativeLuminance(a), relativeLuminance(b)].sort(
    (x, y) => y - x,
  )
  return (lighter + 0.05) / (darker + 0.05)
}

function rgbToHsl([r, g, b]: Rgb): Hsl {
  const red = r / 255
  const green = g / 255
  const blue = b / 255
  const max = Math.max(red, green, blue)
  const min = Math.min(red, green, blue)
  const lightness = (max + min) / 2
  const delta = max - min
  if (delta === 0) {
    return [0, 0, lightness]
  }
  const saturation =
    lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min)
  const hue =
    max === red
      ? ((green - blue) / delta + (green < blue ? 6 : 0)) * 60
      : max === green
        ? ((blue - red) / delta + 2) * 60
        : ((red - green) / delta + 4) * 60
  return [hue, saturation, lightness]
}

function hslToRgb([h, s, l]: Hsl): Rgb {
  const hue = ((h % 360) + 360) % 360
  const chroma = (1 - Math.abs(2 * l - 1)) * s
  const second = chroma * (1 - Math.abs(((hue / 60) % 2) - 1))
  const match = l - chroma / 2
  const segments: Rgb[] = [
    [chroma, second, 0],
    [second, chroma, 0],
    [0, chroma, second],
    [0, second, chroma],
    [second, 0, chroma],
    [chroma, 0, second],
  ]
  const [r, g, b] = segments[Math.floor(hue / 60)]
  return [(r + match) * 255, (g + match) * 255, (b + match) * 255]
}

/**
 * Binary-searches lightness for the value closest to `fill` that still clears
 * `minContrast`. Bounds are white when lightening and black when darkening, both
 * of which clear even 4.5:1 against their own theme's surface, so the search
 * always has a solution to converge on. Candidates are measured after
 * quantization, since rounding a borderline result to 8-bit channels can push it
 * back under the minimum.
 */
function correctLightness(
  fill: Rgb,
  surface: Rgb,
  lighten: boolean,
  minContrast: number,
): Rgb {
  const [hue, saturation, lightness] = rgbToHsl(fill)
  let low = lighten ? lightness : 0
  let high = lighten ? 1 : lightness
  let best: Rgb = lighten ? [255, 255, 255] : [0, 0, 0]

  for (let i = 0; i < 24; i++) {
    const mid = (low + high) / 2
    const candidate = quantize(hslToRgb([hue, saturation, mid]))
    if (contrastRatio(candidate, surface) >= minContrast) {
      best = candidate
      if (lighten) {
        high = mid
      } else {
        low = mid
      }
    } else if (lighten) {
      low = mid
    } else {
      high = mid
    }
  }

  return best
}

function correctedColor(
  fill: Rgb,
  surfaceHex: string,
  lighten: boolean,
  minContrast: number,
): string {
  const surface = parseHex(surfaceHex)
  if (!surface || contrastRatio(fill, surface) >= minContrast) {
    return toHex(fill)
  }
  return toHex(correctLightness(fill, surface, lighten, minContrast))
}

function correctedPair(color: string, minContrast: number): SwatchRingColors {
  const fill = parseHex(color)
  if (!fill) {
    return { light: color, dark: color }
  }
  return {
    light: correctedColor(fill, SWATCH_SURFACE.light, false, minContrast),
    dark: correctedColor(fill, SWATCH_SURFACE.dark, true, minContrast),
  }
}

/**
 * Per-theme outline color for a swatch filled with `color`: the same hue and
 * saturation, lightened (dark theme) or darkened (light theme) only as far as
 * 3:1 against that theme's swatch surface requires. Equals `color` when the fill
 * already clears 3:1, and for a color that is not a hex value — nothing to
 * correct against, so the caller renders no visible ring.
 */
export function getSwatchRingColors(color: string): SwatchRingColors {
  return correctedPair(color, NON_TEXT_CONTRAST)
}

/**
 * Per-theme text color for label text tinted with `color` — the same correction
 * as {@link getSwatchRingColors} held to text's 4.5:1 instead. Use where a
 * user-chosen color is the foreground, such as a tag-tinted filter chip.
 */
export function getReadableTextColors(color: string): SwatchRingColors {
  return correctedPair(color, TEXT_CONTRAST)
}
